import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { themeOrange } from "../../../utils/colorTheme";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../../utils/asyncStatus";
import { useNavigate } from "react-router-dom";
import { PostReviewAsync, updateJobStatusAsync } from "../../../services/services";
import { getImageUrl } from "../../../utils/imageUrl";
import { getServiceLocalImage } from "../../../utils/serviceImages";
import { format } from "date-fns";
import { MdOutlineLocationOn, MdOutlineWatchLater, MdEmail, MdOutlineWork, MdExpandMore, MdExpandLess, MdCancel } from "react-icons/md";
import { HiMiniChatBubbleLeftRight } from "react-icons/hi2";
import { LuCalendarClock } from "react-icons/lu";
import { FaUserCircle } from "react-icons/fa";

const ActiveJobsTab = ({ jobLoader }) => {
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJobStatus, setSelectedJobStatus] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { get_all_jobs_data, job_status_update_status } = useSelector(
    (state) => state.serviceManage
  );

  const jobStatusLoader = job_status_update_status === asyncStatus.LOADING;
  const activeJobs = get_all_jobs_data?.filter((job) => job?.status === "Assigned") || [];

  const handleViewDetails = (jobId) => {
    setSelectedJobId((prev) => (prev === jobId ? null : jobId));
  };

  const _handleVendorChat = (_id, first_name, last_name, avatar) => {
    navigate(`/dashboard/message-user`, {
      state: { id: _id, first_name, last_name, avatar },
    });
  };

  const handleCancel = (id, status) => {
    setSelectedJobStatus(status);
    dispatch(updateJobStatusAsync({ status, id }));
  };

  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return null;
    return format(d, "EEE, MMM d, yyyy");
  };

  const isToday = (dateStr) => {
    const jobDate = parseDate(dateStr);
    if (!jobDate) return false;
    const today = new Date();
    return (
      jobDate.getDate() === today.getDate() &&
      jobDate.getMonth() === today.getMonth() &&
      jobDate.getFullYear() === today.getFullYear()
    );
  };

  const isUpcoming = (dateStr) => {
    const jobDate = parseDate(dateStr);
    if (!jobDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    jobDate.setHours(0, 0, 0, 0);
    return jobDate > today;
  };

  if (!activeJobs.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
        <Box sx={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MdOutlineWork size={36} color="#ccc" />
        </Box>
        <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
          No active jobs found
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {activeJobs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((e) => {
          const isSelected = selectedJobId === e?.id;
          const serviceName = e?.service?.name || "Service";
          const dateStr = e?.date_time_lock?.date;
          const todayJob = isToday(dateStr);
          const upcomingJob = isUpcoming(dateStr);
          const vendorName = `${e?.pro?.first_name || ""} ${e?.pro?.last_name || ""}`.trim();
          const vendorInitials = vendorName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

          return (
            <Box
              key={e?.id}
              sx={{
                borderRadius: "14px",
                border: todayJob ? `2px solid ${themeOrange}` : "1.5px solid #ebebeb",
                backgroundColor: "white",
                overflow: "hidden",
                boxShadow: todayJob ? `0 4px 20px ${themeOrange}25` : "0 2px 8px rgba(0,0,0,0.05)",
                transition: "box-shadow 0.2s",
              }}
            >
              {/* Today banner */}
              {todayJob && (
                <Box sx={{ px: 3, py: 0.75, background: `linear-gradient(90deg, ${themeOrange} 0%, #c53f10 100%)` }}>
                  <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                    TODAY'S JOB
                  </Typography>
                </Box>
              )}

              {/* Main card row */}
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2}>
                {/* Service image */}
                <Box sx={{ width: { xs: "100%", sm: 110 }, height: { xs: 140, sm: 110 }, borderRadius: "10px", overflow: "hidden", flexShrink: 0 }}>
                  <img
                    src={getServiceLocalImage(serviceName)}
                    alt={serviceName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Content */}
                <Stack flex={1} spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a", lineHeight: 1.3 }}>
                      {serviceName}
                    </Typography>
                    <Chip
                      label="Assigned"
                      size="small"
                      sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: "700", fontSize: "11px", border: "1px solid #a5d6a7", flexShrink: 0 }}
                    />
                  </Stack>

                  <Stack spacing={0.75}>
                    {dateStr && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <LuCalendarClock size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: todayJob ? themeOrange : "#666", fontWeight: todayJob ? "600" : "400" }}>
                          {formatDate(dateStr)}
                        </Typography>
                        {e?.date_time_lock?.shift && (
                          <Typography sx={{ fontSize: "12px", color: "#999" }}>
                            ({e.date_time_lock.shift.slice(0, 3)})
                          </Typography>
                        )}
                        {todayJob && (
                          <Chip label="Today" size="small" sx={{ height: "18px", fontSize: "10px", fontWeight: "700", backgroundColor: `${themeOrange}15`, color: themeOrange, border: `1px solid ${themeOrange}40` }} />
                        )}
                        {upcomingJob && !todayJob && (
                          <Chip label="Upcoming" size="small" sx={{ height: "18px", fontSize: "10px", fontWeight: "600", backgroundColor: "#e3f2fd", color: "#1976d2", border: "1px solid #90caf9" }} />
                        )}
                      </Stack>
                    )}
                    {e?.time && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdOutlineWatchLater size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {e.time.length > 60 ? `${e.time.substring(0, 60)}...` : e.time}
                        </Typography>
                      </Stack>
                    )}
                    {e?.location && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdOutlineLocationOn size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }}>
                          {e.location}
                        </Typography>
                      </Stack>
                    )}
                    {vendorName && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: "10px", fontWeight: "700", backgroundColor: "#071d5b", flexShrink: 0 }}>
                          {vendorInitials || <FaUserCircle size={12} />}
                        </Avatar>
                        <Typography sx={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>
                          Pro: {vendorName}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Right: expand */}
                <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} justifyContent="space-between" flexShrink={0} gap={1}>
                  <Box
                    sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: `${themeOrange}10`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={() => handleViewDetails(e?.id)}
                  >
                    {isSelected ? <MdExpandLess size={20} color={themeOrange} /> : <MdExpandMore size={20} color={themeOrange} />}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewDetails(e?.id)}
                    sx={{ borderColor: themeOrange, color: themeOrange, borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: "600", px: 2, "&:hover": { backgroundColor: `${themeOrange}08`, borderColor: themeOrange } }}
                  >
                    {isSelected ? "Hide" : "Details"}
                  </Button>
                </Stack>
              </Stack>

              {/* Expanded section */}
              <Collapse in={isSelected}>
                <Box sx={{ px: 3, pb: 3, borderTop: "1px solid #f0f0f0", pt: 2.5 }}>
                  <Stack spacing={2.5}>

                    {e?.description && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Job Description</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0", borderLeft: `3px solid ${themeOrange}` }}>
                          <Typography sx={{ color: "#555", fontSize: "14px", lineHeight: 1.6 }}>{e.description}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {e?.time && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Timing Constraints</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
                          <Typography sx={{ color: "#555", fontSize: "14px" }}>{e.time}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {e?.vendor?.first_name && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Assigned Professional</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
                          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" gap={2}>
                            <Stack direction="row" alignItems="center" gap={2}>
                              <Avatar sx={{ width: 44, height: 44, backgroundColor: "#071d5b", fontWeight: "700", fontSize: "16px" }}>
                                {vendorInitials}
                              </Avatar>
                              <Stack spacing={0.25}>
                                <Typography sx={{ fontWeight: "600", fontSize: "15px", color: "#1a1a1a" }}>{vendorName}</Typography>
                                {e?.vendor?.email && (
                                  <Stack direction="row" alignItems="center" gap={0.5}>
                                    <MdEmail size={13} color="#999" />
                                    <Typography sx={{ fontSize: "13px", color: "#888" }}>{e.vendor.email}</Typography>
                                  </Stack>
                                )}
                                {dateStr && (
                                  <Typography sx={{ fontSize: "12px", color: "#aaa" }}>
                                    Available: {formatDate(dateStr)} {e?.date_time_lock?.shift || ""}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                            <Button
                              variant="contained"
                              startIcon={<HiMiniChatBubbleLeftRight size={16} />}
                              onClick={() => _handleVendorChat(e?.vendor?.id || "", e?.vendor?.first_name || "", e?.vendor?.last_name || "", e?.vendor?.avatar || "")}
                              sx={{ background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)", borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "13px", boxShadow: "none", "&:hover": { background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)", boxShadow: "none" }, flexShrink: 0 }}
                            >
                              Chat Now
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    )}

                    {e?.images?.length > 0 && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Job Photos</Typography>
                        <Stack spacing={1}>
                          {e.images.map(({ url, caption }) => (
                            <Box key={url} sx={{ p: 1.5, border: "1px solid #f0f0f0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                              <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                  <Box sx={{ height: 80, borderRadius: "8px", overflow: "hidden" }}>
                                    <img src={url} alt={caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  </Box>
                                </Grid>
                                <Grid item xs={9} sm={10}>
                                  <Typography sx={{ color: "#666", fontSize: "13px", lineHeight: 1.5 }}>{caption || ""}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={jobStatusLoader && selectedJobStatus === "Cancelled" ? null : <MdCancel size={16} />}
                        onClick={() => handleCancel(e?.id, "Cancelled")}
                        sx={{ borderColor: "#ef5350", color: "#ef5350", borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "13px", px: 3, "&:hover": { backgroundColor: "#fef2f2", borderColor: "#ef5350" } }}
                      >
                        {jobStatusLoader && selectedJobStatus === "Cancelled" ? (
                          <CircularProgress size={16} sx={{ color: "#ef5350" }} />
                        ) : "Cancel Job"}
                      </Button>
                    </Stack>

                  </Stack>
                </Box>
              </Collapse>
            </Box>
          );
        })}
    </Stack>
  );
};

export default ActiveJobsTab;
